import React from 'react';
import {
  Box,
  Chip,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  AutoGraphRounded,
  GavelRounded,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import CaseResearchAnalytics from './CaseResearchAnalytics';
import CaseArchiveReview from './CaseArchiveReview';

const views = [
  {
    id: 'decisions',
    label: '1. Search cases',
    description: 'Search the complete collection, review reasons, save authorities, and verify the official source.',
    icon: <GavelRounded />,
  },
  {
    id: 'patterns',
    label: '2. Explore patterns',
    description: 'Use explainable classifications to discover groups of cases, then inspect the underlying decisions.',
    icon: <AutoGraphRounded />,
  },
];

export default function ResearchWorkspace() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedView = searchParams.get('view');
  const normalizedView = requestedView === 'search' ? 'decisions' : requestedView;
  const activeView = views.some(view => view.id === normalizedView) ? normalizedView : 'decisions';
  const changeView = (_, nextView) => {
    const next = new URLSearchParams(searchParams);
    next.set('view', nextView);
    next.delete('case');
    next.delete('title');
    next.delete('citation');
    setSearchParams(next);
  };

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', flexShrink: 0 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ md: 'center' }}
          spacing={2}
          sx={{ px: { xs: 2, md: 2.5 }, pt: 2.25, pb: 1.75 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h5" sx={{ fontWeight: 820, letterSpacing: '-.03em' }}>Case research</Typography>
              <Chip size="small" color="success" variant="outlined" label="155,190 decisions" />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45 }}>
              Search, read, save and verify Canadian authorities in one continuous workspace. Pattern analysis always links back to the underlying decisions.
            </Typography>
          </Box>
          <Chip
            size="small"
            variant="outlined"
            label={activeView === 'decisions' ? 'Start here: find and verify authority' : 'Discovery view: open every result in context'}
            sx={{ display: { xs: 'none', md: 'inline-flex' } }}
          />
        </Stack>
        <Tabs
          value={activeView}
          onChange={changeView}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Case research workflow"
          sx={{ px: { xs: 1, md: 1.5 }, borderTop: '1px solid', borderColor: 'divider' }}
        >
          {views.map(view => (
            <Tab key={view.id} value={view.id} icon={view.icon} iconPosition="start" label={view.label} />
          ))}
        </Tabs>
      </Paper>

      <Box sx={{ flex: 1, minHeight: 0, pt: 2 }}>
        {activeView === 'patterns' && <CaseResearchAnalytics embedded />}
        {activeView === 'decisions' && <CaseArchiveReview embedded />}
      </Box>
    </Box>
  );
}
